// Content Tab (Sponsor Groups & Testimonials) - nested DnD
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import { ImageUpload } from '../../../components/ui/image-upload';
import { Plus, X, Building2, Quote, GripVertical } from 'lucide-react';

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ─── Sortable sponsor item (inside a group) ─── */
const SortableSponsorItem = ({ groupIdx, itemIdx, item, updateSponsorItem, removeSponsorItem }) => {
  const id = `item-${groupIdx}-${itemIdx}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white/5 border border-white/10 rounded-lg p-3 space-y-2 ${isDragging ? 'ring-1 ring-primary/40' : ''}`}
      data-testid={`sponsor-item-${groupIdx}-${itemIdx}`}
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground"
          aria-label="Trascina"
          data-testid={`sponsor-item-drag-${groupIdx}-${itemIdx}`}
        >
          <GripVertical size={14} />
        </button>
        <Button type="button" variant="ghost" size="sm" onClick={() => removeSponsorItem(groupIdx, itemIdx)} className="h-6 w-6 p-0 text-destructive">
          <X size={12} />
        </Button>
      </div>
      <Input
        value={item.name || ''}
        onChange={(e) => updateSponsorItem(groupIdx, itemIdx, 'name', e.target.value)}
        placeholder="Nome sponsor"
        className="bg-white/5 border-white/10 h-8 text-sm"
      />
      <Input
        value={item.url || ''}
        onChange={(e) => updateSponsorItem(groupIdx, itemIdx, 'url', e.target.value)}
        placeholder="URL sito"
        className="bg-white/5 border-white/10 h-8 text-sm"
      />
      <ImageUpload
        value={item.logo}
        onChange={(url) => updateSponsorItem(groupIdx, itemIdx, 'logo', url)}
        label="Logo"
      />
    </div>
  );
};

/* ─── Sortable sponsor group (container) ─── */
const SortableSponsorGroup = ({ group, groupIdx, updateSponsorGroup, removeSponsorGroup, addSponsorItem, updateSponsorItem, removeSponsorItem, reorderSponsorItems }) => {
  const id = group.id || `group-${groupIdx}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleItemDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = parseInt(String(active.id).split('-').pop(), 10);
    const newIdx = parseInt(String(over.id).split('-').pop(), 10);
    if (Number.isNaN(oldIdx) || Number.isNaN(newIdx)) return;
    reorderSponsorItems(groupIdx, oldIdx, newIdx);
  };

  const items = group.items || [];
  const itemIds = items.map((_, i) => `item-${groupIdx}-${i}`);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`glass rounded-xl p-4 border border-white/10 space-y-4 ${isDragging ? 'ring-2 ring-primary/40' : ''}`}
      data-testid={`sponsor-group-${groupIdx}`}
    >
      {/* Group header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground"
          aria-label="Trascina gruppo"
          data-testid={`sponsor-group-drag-${groupIdx}`}
        >
          <GripVertical size={18} />
        </button>
        <Input
          value={group.name || ''}
          onChange={(e) => updateSponsorGroup(groupIdx, 'name', e.target.value)}
          placeholder="Nome tipologia (es. Main Partner)"
          className="bg-white/5 border-white/10 font-semibold flex-1"
          data-testid={`sponsor-group-name-${groupIdx}`}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => removeSponsorGroup(groupIdx)}
          className="h-8 w-8 p-0 text-destructive"
          data-testid={`sponsor-group-remove-${groupIdx}`}
        >
          <X size={16} />
        </Button>
      </div>

      {/* Items grid (sortable within group) */}
      <DndContext sensors={itemSensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd}>
        <SortableContext items={itemIds} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((item, itemIdx) => (
              <SortableSponsorItem
                key={`item-${groupIdx}-${itemIdx}`}
                groupIdx={groupIdx}
                itemIdx={itemIdx}
                item={item}
                updateSponsorItem={updateSponsorItem}
                removeSponsorItem={removeSponsorItem}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => addSponsorItem(groupIdx)}
        className="w-full"
        data-testid={`sponsor-group-add-item-${groupIdx}`}
      >
        <Plus size={14} className="mr-1" />
        Aggiungi Sponsor
      </Button>
    </div>
  );
};

export const ContentTab = ({
  formData,
  addSponsorGroup,
  updateSponsorGroup,
  removeSponsorGroup,
  reorderSponsorGroups,
  addSponsorItem,
  updateSponsorItem,
  removeSponsorItem,
  reorderSponsorItems,
  addTestimonial,
  updateTestimonial,
  removeTestimonial
}) => {
  const groupSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const groups = formData.sponsor_groups || [];

  const handleGroupDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = groups.findIndex((g, i) => (g.id || `group-${i}`) === active.id);
    const newIdx = groups.findIndex((g, i) => (g.id || `group-${i}`) === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    reorderSponsorGroups(oldIdx, newIdx);
  };

  const groupIds = groups.map((g, i) => g.id || `group-${i}`);

  return (
    <div className="space-y-6">
      {/* Sponsor Groups */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 size={18} className="text-primary" />
            <Label className="text-base font-semibold">Sponsor</Label>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addSponsorGroup} data-testid="add-sponsor-group-btn">
            <Plus size={14} className="mr-1" />
            Nuova Tipologia
          </Button>
        </div>

        {groups.length === 0 && (
          <div className="text-center py-8 text-muted-foreground border border-dashed border-white/10 rounded-lg">
            <p className="text-sm">Nessuna tipologia di sponsor</p>
            <p className="text-xs mt-1">Clicca "Nuova Tipologia" per crearne una (es. Main Partner)</p>
          </div>
        )}

        {groups.length > 0 && (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              Trascina <GripVertical size={12} className="inline -mt-0.5" /> sui contenitori per riordinare le tipologie, o sui singoli sponsor per riordinarli all'interno di una tipologia.
            </p>
            <DndContext sensors={groupSensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
              <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-4" data-testid="sponsor-groups-list">
                  {groups.map((group, idx) => (
                    <SortableSponsorGroup
                      key={group.id || `group-${idx}`}
                      group={group}
                      groupIdx={idx}
                      updateSponsorGroup={updateSponsorGroup}
                      removeSponsorGroup={removeSponsorGroup}
                      addSponsorItem={addSponsorItem}
                      updateSponsorItem={updateSponsorItem}
                      removeSponsorItem={removeSponsorItem}
                      reorderSponsorItems={reorderSponsorItems}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}
      </div>

      {/* Testimonials */}
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Quote size={18} className="text-primary" />
            <Label className="text-base font-semibold">Testimonials</Label>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addTestimonial}>
            <Plus size={14} className="mr-1" />
            Aggiungi
          </Button>
        </div>
        <div className="space-y-3">
          {formData.testimonials.map((testimonial, index) => (
            <div key={`testimonial-${testimonial.author || index}`} className="glass rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Testimonial {index + 1}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeTestimonial(index)} className="h-6 w-6 p-0 text-destructive">
                  <X size={14} />
                </Button>
              </div>
              <Textarea
                value={testimonial.quote}
                onChange={(e) => updateTestimonial(index, 'quote', e.target.value)}
                placeholder="Citazione..."
                className="bg-white/5 border-white/10 min-h-16"
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  value={testimonial.author}
                  onChange={(e) => updateTestimonial(index, 'author', e.target.value)}
                  placeholder="Nome autore"
                  className="bg-white/5 border-white/10"
                />
                <Input
                  value={testimonial.role}
                  onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                  placeholder="Ruolo"
                  className="bg-white/5 border-white/10"
                />
              </div>
              <Input
                value={testimonial.company}
                onChange={(e) => updateTestimonial(index, 'company', e.target.value)}
                placeholder="Azienda"
                className="bg-white/5 border-white/10"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
